/**
 * help.ts — categorized help, deep help, examples.
 *
 * Phase CG. The `help` command is the developer's first lesson — every
 * command listed with syntax, one-line description, and a runnable "→ try"
 * example. Each category is a coloured card.
 */

import { CATEGORY_COLOR, type Category, esc } from "./formatters.js";

export interface CmdSpec {
  name: string;
  /** Argument hint: "<op> [k=v ...]" — appended to name when rendering. */
  args?: string;
  /** One-line description. */
  desc: string;
  /** Runnable example. */
  example?: string;
  /** Cost preview (complexity-priced operation names) for paid endpoints. */
  cost?: string;
  /** Multi-line deep help shown by `help <command>`. */
  deep?: string;
}

export interface CmdGroup {
  category: Category;
  title: string;
  blurb: string;
  cmds: CmdSpec[];
}

export const GROUPS: CmdGroup[] = [
  {
    category: "tutorial",
    title: "Onboarding & meta",
    blurb: "Learn the framework from inside this terminal.",
    cmds: [
      { name: "tutorial",  desc: "Guided 7-step onboarding — the clock, mint your identity + machine (account equation), first compute, verify the receipt, the entangled state, link the SDK, go build. Progress is saved across sessions.", example: "tutorial" },
      { name: "help",      args: "[command]", desc: "This card · or deep help for one command (e.g. `help compute`).", example: "help compute" },
      { name: "examples",  desc: "20+ copy-paste real examples across all categories.", example: "examples" },
      { name: "docs",      args: "<topic>",   desc: "The SDK page for a topic (browser: opens it · native: prints the URL).", example: "docs operators" },
      { name: "doctor",    desc: "Diagnose this install: origin health, clock drift (Zeqonds), auth, binary vs the origin's published sha256 pin, conformance surface. Every ✗ carries its remedy.", example: "doctor" },
      { name: "hello",     desc: "Real one-shot demo: runs `compute NM19 mass=5 acceleration=2` and prints the CKO envelope.", example: "hello" },
      { name: "hello",     args: "universe",  desc: "Runs three operators across QM, NM, GR — sells the framework in one command.", example: "hello universe", cost: "complexity-priced (3 computes)" },
      { name: "version",   desc: "CLI version + framework constants (1.287 Hz, τ=0.777 s, ≤0.1%, operator count).", example: "version" },
      { name: "clear",     desc: "Clears the feed (history retained).", example: "clear" },
      { name: "history",   args: "[clear]",   desc: "Last 50 commands (use ↑/↓ to recall).", example: "history" },
      { name: "exit",      desc: "Leave the terminal (browser: closes the drawer · native REPL: ends the session).", example: "exit" },
      { name: "completion", args: "<zsh|bash>", desc: "Print a shell completion script for the native CLI (the REPL has Tab completion built in).", example: "completion zsh" },
    ],
  },
  {
    category: "identity",
    title: "Identity & credits",
    blurb: "Who you are, what you own, what you can spend.",
    cmds: [
      { name: "signup",  args: '"<phrase>" [name]', desc: "Create your account from the terminal — the 7-step wizard derives your ACCOUNT EQUATION from the phrase (≥4 meaningful words), registers only its hash, and mints your ZID + state machine + api key. The equation is shown once: store it.", example: 'signup "kepler orbits traced in winter starlight"', cost: "creates a new identity" },
      { name: "signin",  args: '<ZID> ["equation"]', desc: "Sign in to an existing identity with its equation. Native CLI: omit the equation for a hidden prompt (never echoed, never in history). The equation must match exactly.", example: "signin ZEQ07100437483" },
      { name: "whoami",  desc: "Current ZID, machine slug, tier, ZEQ balance.", example: "whoami" },
      { name: "credits", desc: "Your credits + ZEQ envelopes, daily spend, recent ledger.", example: "credits" },
      { name: "credits", args: "swap <btc|eth> <amount>", desc: "Swap BTC/ETH → ZEQ (foundation pot).", example: "credits swap btc 0.001" },
      { name: "credits", args: "tally",        desc: "Open the /tally/ balance viewer.",       example: "credits tally" },
      { name: "send",    desc: "List the ZEQ envelope coins you hold (the transferable asset; credits are immovable fuel).", example: "send" },
      { name: "send",    args: "<token_id> <ZID> [--public|--private]", desc: "Transfer a whole envelope coin to another ZID. Visibility follows your machine default (see `send default`) unless you pass --public/--private. --public publishes the transfer's proof-of-compute receipt to this domain's observer; the value moves either way.", example: "send ZT-… ZEQ07263199779 --public" },
      { name: "send default", args: "[public|private]", desc: "View or set this machine's publish default for transfers. Default private. `send default public` makes every transfer public unless you pass --private.", example: "send default public" },
      { name: "send claims", desc: "List coins you hold that are HOMED on another domain (cross-domain claims).", example: "send claims" },
      { name: "send claim", args: "<token_id> <ZID> [to_origin]", desc: "Move a claimed (peer-homed) coin onward. Routed via your domain to the coin's home chain using your stored claim ticket — no account needed on the home.", example: "send claim ZT-… ZEQ07263199779" },
      { name: "send claim publish", args: "<token_id>", desc: "Publish a held claim's receipt to THIS domain's observer as a public, home-anchored row — verifiable against the coin's home chain. Default private.", example: "send claim publish ZT-…" },
      { name: "signout", desc: "Sign out of this browser session (machine survives).", example: "signout" },
      { name: "keys",        desc: "List this machine's developer API keys (prefixes + metadata, no raw values).", example: "keys" },
      { name: "keys mint",   args: "<name> [read|write|admin]", desc: "Mint a headless API key — the Bearer an app/native CLI uses for /api/zeq/compute. Raw key shown once.", example: "keys mint my-app write", cost: "owner-gated" },
      { name: "keys revoke", args: "<id>", desc: "Revoke an API key by id.", example: "keys revoke <id>" },
      { name: "machine mint", args: '"<equation>" [name]', desc: "Spin up a NEW state machine (new ZID) from an equation YOU supply — the manual genesis flow (`signup` derives one for you). The equation is the identity secret.", example: 'machine mint "F = m*a" My Machine', cost: "creates a new identity" },
      { name: "sdk", desc: "SDK quickstart wired to YOUR key + this origin — REST curl, JavaScript, Python, MCP server, kernel skill, native CLI install.", example: "sdk" },
      { name: "conformance", desc: "Prove this node computes the golden physics — fetches the content-hashed vector corpus, has the node re-run every vector through its §3 recompute path, and bit-compares (IEEE-754 identity). A single mutated constant fails it.", example: "conformance" },
      { name: "chain verify", desc: "Proof-of-Elapsed-Zeqonds verification. Native: runs the canonical zero-dependency offline verifier (VDF spine, elapsed Zeqonds, binding, hash links, Ed25519). Browser: the hash-link leg + pointer to the full check.", example: "chain verify" },
    ],
  },
  {
    category: "pulse",
    title: "Pulse / Zeqond",
    blurb: "The 1.287 Hz HulyaPulse heartbeat — the framework's native clock.",
    cmds: [
      { name: "pulse",        desc: "Current Zeqond, phase, HulyaPulse status.", example: "pulse" },
      { name: "pulse clock",  desc: "Live ticking clock (Ctrl-C or any key to stop).", example: "pulse clock" },
      { name: "pulse status", desc: "Full state of the Pulse on this machine.", example: "pulse status" },
      { name: "time",         desc: "Current local time, Zeqond, and Unix epoch.", example: "time" },
    ],
  },
  {
    category: "compute",
    title: "Kernel & operators",
    blurb: "The 1,500+-operator kernel. Run any operator, list them, search them.",
    cmds: [
      { name: "compute",   args: "<op> [k=v ...]",  desc: "Run an operator and get a signed CKO envelope. KO42 carrier auto-applied. Append `--json` for machine-readable output.", example: "compute NM19 mass=5 acceleration=2", cost: "complexity-priced in credits" },
      { name: "compute",   args: "<op> [k=v ...] --dry-run", desc: "Cost-only preview — what it WOULD cost. Nothing charged, no envelope minted.", example: "compute NM19 mass=5 acceleration=2 --dry-run" },
      { name: "operators count",                    desc: "Total operator count across all 64 domains.", example: "operators count" },
      { name: "operators list",   args: "[domain]", desc: "List operators (paginated, group by domain).", example: "operators list Newtonian Mechanics" },
      { name: "operators search", args: "<query>",  desc: "Fuzzy search the catalogue.", example: "operators search schrodinger" },
      { name: "operators show",   args: "<id>",     desc: "Operator detail card.", example: "operators show NM21" },
      { name: "operators",        args: "categories", desc: "All 64 domain categories.", example: "operators categories" },
    ],
  },
  {
    category: "agent",
    title: "Agents (LLM hyper-agents)",
    blurb: "Spawn an agent, tick it, watch its mesh.",
    cmds: [
      { name: "agents",       desc: "List agents on this machine.", example: "agents" },
      { name: "agents spawn", args: '"<directive>"', desc: "Spawn a new hyper-agent.", example: 'agents spawn "find the lowest-energy orbit"', cost: "complexity-priced (agent_spawn)" },
      { name: "agents tick",  args: "<id>",  desc: "Manually tick an agent's loop.", example: "agents tick zag_…", cost: "complexity-priced (agent_tick)" },
      { name: "agents kill",  args: "<id>",  desc: "Kill an agent (writes a contract transition).", example: "agents kill zag_…" },
      { name: "agents mesh",  args: "<id>",  desc: "Show the agent's mesh links.", example: "agents mesh zag_…" },
    ],
  },
  {
    category: "skill",
    title: "Skills (LLM-synthesised procedures)",
    blurb: "Synthesise new mini-procedures from natural-language intent.",
    cmds: [
      { name: "skills",            desc: "List synthesised skills on this machine.", example: "skills" },
      { name: "skills synthesise", args: '"<intent>"', desc: "Synthesise a new skill.", example: 'skills synthesise "compute orbital velocity for any planet"', cost: "complexity-priced (skill_synthesis)" },
    ],
  },
  {
    category: "contract",
    title: "Contracts (state machines / queries)",
    blurb: "Deploy contracts, fire them, dry-run them, inspect the audit trail.",
    cmds: [
      { name: "contract list",        desc: "All contracts on this machine.", example: "contract list" },
      { name: "contract show",        args: "<id>", desc: "Contract detail.", example: "contract show ct_…" },
      { name: "contract fire-now",    args: "<id>", desc: "Force a transition right now.", example: "contract fire-now ct_…", cost: "complexity-priced (contract_execution)" },
      { name: "contract pause",       args: "<id>", desc: "Pause the contract.", example: "contract pause ct_…" },
      { name: "contract resume",      args: "<id>", desc: "Resume.", example: "contract resume ct_…" },
      { name: "contract dry-run",     args: "<id>", desc: "Simulate without committing.", example: "contract dry-run ct_…" },
      { name: "contract preview",     args: "<id>", desc: "Live reactor preview.", example: "contract preview ct_…" },
      { name: "contract next-fires",  args: "<id>", desc: "Next 5 predicted firings.", example: "contract next-fires ct_…" },
      { name: "contract export",      args: "<id>", desc: "Export contract JSON.", example: "contract export ct_…" },
      { name: "contract deploy",      args: "<json>", desc: "Deploy a new contract from a definition object.", example: 'contract deploy {"name":"daily","states":["idle","fired"],"initial_state":"idle"}', cost: "complexity-priced (contract_deploy)" },
      { name: "contract import",      args: "<json>", desc: "Import an exported contract definition (round-trips with export).", example: "contract import {…}" },
      { name: "contract events",      args: "[--tag X]", desc: "Recent emit events.", example: "contract events" },
    ],
  },
  {
    category: "state",
    title: "Entangled-state explorer",
    blurb: "The hash-linked entangled state — every transition on this machine.",
    cmds: [
      { name: "state head",        desc: "Top of the entangled-state log.", example: "state head" },
      { name: "state audit",       args: "[--limit N]", desc: "Recent audit rows.", example: "state audit --limit 10" },
      { name: "state public",      desc: "Public-tier audit view.", example: "state public" },
      { name: "state explore",     desc: "Combined entangled-state explorer.", example: "state explore" },
    ],
  },
  {
    category: "api",
    title: "External APIs",
    blurb: "Register external APIs to call from contracts and agents.",
    cmds: [
      { name: "apis",           desc: "List registered external APIs.", example: "apis" },
      { name: "apis add",       args: "<name> <url>", desc: "Register a new API.", example: "apis add weather https://api.weather.gov/..." },
      { name: "apis test",      args: "<id>", desc: "Invoke an API.", example: "apis test api_…" },
      { name: "apis remove",    args: "<id>", desc: "Remove an API.", example: "apis remove api_…" },
    ],
  },
  {
    category: "site",
    title: "Site hosting / PRD",
    blurb: "Publish HTML, manage the PRD that drives the agent.",
    cmds: [
      { name: "site list",     desc: "Published pages on this machine.", example: "site list" },
      { name: "site deploy",   args: "<page> <base64-html> [title]", desc: "Build & publish a live page — same path as the Workbench. html = btoa(yourHTML).", example: 'site deploy hello aGVsbG8= My Page', cost: "owner-gated" },
      { name: "site get",      args: "<page>", desc: "Fetch a published page (title, version, html).", example: "site get hello" },
      { name: "site unpublish",args: "<page>", desc: "Take a published page offline.", example: "site unpublish hello" },
      { name: "prd show",      desc: "Print this machine's PRD.", example: "prd show" },
      { name: "prd edit",      args: '"<markdown>"', desc: "Replace the machine's PRD (the build directive the agent reads).", example: 'prd edit "Build a tide-clock app"' },
      { name: "prd push",      desc: "Send PRD to the agent as a build directive.", example: "prd push" },
    ],
  },
  {
    category: "system",
    title: "Net / utility / oracle",
    blurb: "Health, transparency, raw net.",
    cmds: [
      { name: "health",   desc: "api-core health probe.", example: "health" },
      { name: "oracle",   desc: "Transparency Oracle now: supply, foundation, BTC/ETH price, active machines.", example: "oracle" },
      { name: "oracle",   args: "history [N]", desc: "Last N Zeqonds of supply state.", example: "oracle history 10" },
      { name: "web",      args: "<url>", desc: "Fetch via the SDK proxy (same-origin).", example: "web https://api.weather.gov/..." },
      { name: "theme",    args: "[name]", desc: "Switch CLI theme (cryptopunk / paper / mono).", example: "theme paper" },
    ],
  },
  // Phase CI — internal Zeq API surface (138 endpoints across 31 groups)
  {
    category: "api",
    title: "Zeq API surface (138 endpoints · 31 groups)",
    blurb: "Browse the framework's public endpoint surface from the terminal — every group, every count.",
    cmds: [
      { name: "api",          desc: "List all 31 API groups with endpoint counts.", example: "api" },
      { name: "api groups",   desc: "Same as `api` — explicit form.", example: "api groups" },
      { name: "api show",     args: "<group>", desc: "Open the SDK page for one group in a new tab.", example: "api show chain" },
      { name: "api search",   args: "<query>", desc: "Find a group by name or description.", example: "api search audit" },
      { name: "api total",    desc: "Quick endpoint / group counts.", example: "api total" },
    ],
  },
  // Phase CI — protocols surface (236 protocols across 9 themes + 50 domains)
  {
    category: "system",
    title: "Protocols (236 across 9 themes)",
    blurb: "Time · Identity · Key · Encryption · Transport · Compile · Consensus · Measurement · Governance.",
    cmds: [
      { name: "protocols",        desc: "List the 9 thematic chapters with anchor operators.", example: "protocols" },
      { name: "protocols themes", desc: "Same as `protocols` — explicit form.", example: "protocols themes" },
      { name: "protocols domains", desc: "List all ~50 protocol domains (Agriculture · Audio · … · Weather).", example: "protocols domains" },
      { name: "protocols show",   args: "<n|name>", desc: "Open the chapter page (e.g. `2` or `identity-access`).", example: "protocols show 2" },
      { name: "protocols search", args: "<query>", desc: "Find themes or domains containing the query.", example: "protocols search consensus" },
    ],
  },
  // Phase CI — operator family taxonomy (8 families · 1,500+ operators · 67 domains)
  {
    category: "compute",
    title: "Operator families (8 · 1,500+ ops · 67 domains)",
    blurb: "Quantum · Newtonian · GR · CS · Awareness · Hulyas Forensics · Agentic · Zeq Resonance.",
    cmds: [
      { name: "families",       desc: "List all 8 operator families with their ID ranges.", example: "families" },
      { name: "families list",  desc: "Same as `families`.", example: "families list" },
      { name: "families show",  args: "<slug>", desc: "Open the SDK page for one family.", example: "families show quantum-mechanics" },
    ],
  },
  // Phase CI — Kernel Skill (the universal LLM-client boot prompt)
  {
    category: "skill",
    title: "Kernel Skill — zeq-os-mi-kernel",
    blurb: "The Zeq kernel as a SKILL.md any LLM client (Claude Code, Cowork, Cursor, OpenAI) can read.",
    cmds: [
      { name: "skill",          desc: "Show the 3 canonical endpoints (prompt / md / manifest).", example: "skill" },
      { name: "skill prompt",   desc: "Fetch the raw boot sequence (text/plain) — paste into a system prompt.", example: "skill prompt" },
      { name: "skill md",       desc: "Fetch the full SKILL.md with YAML frontmatter.", example: "skill md" },
      { name: "skill manifest", desc: "JSON manifest with version + sha256 + per-platform install steps.", example: "skill manifest" },
      { name: "skill install",  args: "<client>", desc: "Print install snippet for claude-code / cowork / cursor / openai.", example: "skill install claude-code" },
      { name: "skill verify",   desc: "Show the sha256 from the manifest (cross-check against your local copy).", example: "skill verify" },
    ],
  },
  // Phase Ω4 — ZSC (Zeq Secure Context). Replaces .env for production secrets.
  {
    category: "system",
    title: "Context · ZSC (Zeq Secure Context)",
    blurb: "Encrypted at-rest secret vault. Every read is an entangled-state audit row. Replaces .env.",
    cmds: [
      { name: "context",        desc: "Same as `context list` — show all secrets (names + metadata only, never values).", example: "context" },
      { name: "context list",   desc: "List all secrets with read counts, last-read Zeqond, owner ZID.", example: "context list" },
      { name: "context info",   args: "<NAME>", desc: "Single-secret metadata: purpose, expiry, permissions, entangled-state audit summary.", example: "context info STRIPE_SECRET_KEY" },
      { name: "context set",    args: "<NAME> <VALUE>", desc: "Encrypt + store. Value goes through AES-256-GCM with the framework's PBKDF2-HULYAS key.", example: "context set MONGODB_URI mongodb+srv://..." },
      { name: "context rotate", args: "<NAME>", desc: "Re-encrypt with a fresh IV. Same plaintext, new ciphertext on disk.", example: "context rotate STRIPE_SECRET_KEY" },
      { name: "context grant",  args: "<NAME> <ZID>", desc: "Add a ZID to the permissions array (Phase Ω6 enforces; Ω4 records).", example: "context grant STRIPE_SECRET_KEY ZEQ07000000000" },
      { name: "context revoke", args: "<NAME> <ZID>", desc: "Remove a ZID from permissions.", example: "context revoke STRIPE_SECRET_KEY ZEQ07000000000" },
      { name: "context audit",  args: "<NAME> [N]", desc: "Last N audit_log rows for this secret (transition_id, zeqond, proof_digest).", example: "context audit STRIPE_SECRET_KEY 20" },
      { name: "context delete", args: "<NAME>", desc: "Permanently delete a secret. Irreversible. The entangled state is retained.", example: "context delete OLD_TOKEN" },
    ],
  },
];

/** Flat list of every primary command name — for autocomplete + closest-match. */
export function commandNames(): string[] {
  const out = new Set<string>();
  for (const g of GROUPS) {
    for (const c of g.cmds) {
      const head = c.name.split(/\s+/)[0];
      out.add(head);
    }
  }
  return Array.from(out).sort();
}

/** Render the categorised help as a DOM fragment (mounts inside the feed). */
export function renderHelp(): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "cli-help";
  for (const g of GROUPS) {
    const colour = CATEGORY_COLOR[g.category];
    const section = document.createElement("div");
    section.className = "cli-help-group";
    section.style.setProperty("--cli-cat-color", colour);
    let html =
      '<div class="cli-help-group-head">' +
        '<span class="cli-help-cat" style="color:' + colour + ';border-color:' + colour + ';">' + esc(g.title) + '</span>' +
        '<span class="cli-help-blurb">' + esc(g.blurb) + '</span>' +
      '</div>';
    for (const c of g.cmds) {
      const sig = c.name + (c.args ? ' <span class="cli-help-args">' + esc(c.args) + '</span>' : '');
      const cost = c.cost ? '<span class="cli-help-cost">(' + esc(c.cost) + ')</span>' : '';
      const ex = c.example ? '<div class="cli-help-ex">→ try: <code>' + esc(c.example) + '</code></div>' : '';
      html +=
        '<div class="cli-help-cmd">' +
          '<div class="cli-help-row"><span class="cli-help-sig" style="color:' + colour + ';">' + sig + '</span> ' + cost + '</div>' +
          '<div class="cli-help-desc">' + esc(c.desc) + '</div>' +
          ex +
        '</div>';
    }
    section.innerHTML = html;
    wrap.appendChild(section);
  }
  const foot = document.createElement("div");
  foot.className = "cli-help-foot";
  foot.innerHTML =
    '→ <code>help &lt;command&gt;</code> for deep help · ' +
    '<code>tutorial</code> to learn from scratch · ' +
    '<code>docs &lt;topic&gt;</code> to open SDK pages · ' +
    '<code>hello universe</code> for a working framework demo';
  wrap.appendChild(foot);
  return wrap;
}

/** Find a command by primary name across all groups. */
export function findCommand(name: string): { group: CmdGroup; cmd: CmdSpec } | null {
  const n = name.toLowerCase();
  for (const g of GROUPS) {
    for (const c of g.cmds) {
      if (c.name.split(/\s+/)[0].toLowerCase() === n) return { group: g, cmd: c };
    }
  }
  return null;
}

/** Deep help for one command — table of every variant + deep explanation. */
export function renderDeepHelp(name: string): HTMLElement | null {
  const hit = findCommand(name);
  if (!hit) return null;
  const colour = CATEGORY_COLOR[hit.group.category];
  const variants: CmdSpec[] = [];
  for (const g of GROUPS) for (const c of g.cmds) {
    if (c.name.split(/\s+/)[0].toLowerCase() === name.toLowerCase()) variants.push(c);
  }
  const el = document.createElement("div");
  el.className = "cli-card cli-card-help";
  el.style.setProperty("--cli-cat-color", colour);
  let html =
    '<div class="cli-card-head">' +
      '<span class="cli-card-tag" style="color:' + colour + ';border-color:' + colour + ';">' + esc(hit.group.category.toUpperCase()) + '</span>' +
      '<span class="cli-card-title">help · <code>' + esc(name) + '</code></span>' +
    '</div>' +
    '<div class="cli-card-body">';
  for (const v of variants) {
    const sig = v.name + (v.args ? ' ' + v.args : '');
    html +=
      '<div class="cli-help-cmd">' +
        '<div class="cli-help-row"><span class="cli-help-sig" style="color:' + colour + ';">' + esc(sig) + '</span>' +
        (v.cost ? ' <span class="cli-help-cost">(' + esc(v.cost) + ')</span>' : '') + '</div>' +
        '<div class="cli-help-desc">' + esc(v.desc) + '</div>' +
        (v.example ? '<div class="cli-help-ex">→ try: <code>' + esc(v.example) + '</code></div>' : '') +
        (v.deep ? '<div class="cli-help-deep">' + esc(v.deep) + '</div>' : '') +
      '</div>';
  }
  html += '</div>';
  el.innerHTML = html;
  return el;
}
